import { ref, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import { useI18n } from './useI18n';

/**
 * Composable для мемоизации тяжелых вычислений
 * Кеширует результаты функций до тех пор, пока зависимости не изменятся
 */
export function useMemo<T>(
  fn: () => T,
  deps: () => unknown[]
): ComputedRef<T> {
  const cache = ref<T | undefined>(undefined);
  let lastDeps: unknown[] = [];

  return computed(() => {
    const currentDeps = deps();
    const depsChanged = currentDeps.length !== lastDeps.length ||
      currentDeps.some((dep, i) => dep !== lastDeps[i]);

    if (depsChanged || cache.value === undefined) {
      lastDeps = currentDeps;
      cache.value = fn();
    }

    return cache.value as T;
  });
}

/**
 * Кеш для парсинга gift сообщений
 */
const giftCache = new Map<string, { isGift: boolean; parsed: any }>();
const giftRegex = new RegExp('\\[gift\\](.*?)\\[/gift\\]');

export function useGiftMessageCache() {
  const parseGiftMessage = (text: string | undefined): { isGift: boolean; parsed: any } => {
    if (!text) return { isGift: false, parsed: {} };
    
    const cached = giftCache.get(text);
    if (cached) return cached;

    const isGift = giftRegex.test(text);
    let parsed = {};
    
    if (isGift) {
      const match = text.match(giftRegex);
      if (match) {
        try {
          parsed = JSON.parse(match[1]);
        } catch {
          parsed = {};
        }
      }
    }

    const result = { isGift, parsed };
    
    // Ограничиваем размер кеша (LRU)
    if (giftCache.size > 1000) {
      const firstKey = giftCache.keys().next().value;
      if (firstKey) giftCache.delete(firstKey);
    }
    
    giftCache.set(text, result);
    return result;
  };

  const clearGiftCache = () => giftCache.clear();

  return { parseGiftMessage, clearGiftCache };
}

/**
 * Кеш для attachment типов
 */
const attachmentTypeCache = new Map<string, boolean>();

export function useAttachmentCache() {
  const isImageAttachment = (attachment: any): boolean => {
    if (!attachment) return false;
    
    const key = JSON.stringify({
      type: attachment.type,
      mime: attachment.mime || attachment.content_type,
      url: attachment.url || attachment.preview_url || attachment.thumbnail
    });

    const cached = attachmentTypeCache.get(key);
    if (cached !== undefined) return cached;

    const type = String(attachment.type || '').toLowerCase();
    const mime = String(attachment.mime || attachment.content_type || '').toLowerCase();
    const url = String(attachment.url || attachment.preview_url || attachment.thumbnail || '').toLowerCase();

    const result = type === 'image' || type === 'photo' ||
      type.startsWith('image/') ||
      mime.startsWith('image/') ||
      !!(attachment.thumbnail || attachment.preview_url) ||
      /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(url);

    if (attachmentTypeCache.size > 500) {
      const firstKey = attachmentTypeCache.keys().next().value;
      if (firstKey) attachmentTypeCache.delete(firstKey);
    }

    attachmentTypeCache.set(key, result);
    return result;
  };

  const clearAttachmentCache = () => attachmentTypeCache.clear();

  return { isImageAttachment, clearAttachmentCache };
}

/**
 * Оптимизированная группировка сообщений
 */
export interface MessageGroup {
  display_date: string;
  messages: any[];
}

export function useMessageGrouping(messages: Ref<any[]>) {
  const { t } = useI18n();
  // Используем computed для полной реактивности - как в оригинале до рефакторинга
  const grouped = computed<MessageGroup[]>(() => {
    const msgs = messages.value || [];
    const groups: Record<string, MessageGroup> = {};
    const locale = document.documentElement.lang || 'en';
    
    for (const m of msgs) {
      const timestamp = m.timestamp || m.ts;
      if (!timestamp) continue;
      
      const d = new Date(timestamp * 1000);
      const key = d.toDateString();
      
      if (!groups[key]) {
        let displayDate = key;
        
        if (window.TimezoneHelper?.getDisplayDate) {
          try {
            displayDate = window.TimezoneHelper.getDisplayDate(d);
          } catch {
            displayDate = key;
          }
        } else {
          const today = new Date();
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          
          if (d.toDateString() === today.toDateString()) {
            displayDate = t('messages.date_today');
          } else if (d.toDateString() === yesterday.toDateString()) {
            displayDate = t('messages.date_yesterday');
          } else {
            displayDate = d.toLocaleDateString(locale, { 
              day: 'numeric', 
              month: 'long',
              year: 'numeric'
            });
          }
        }
        
        groups[key] = { display_date: displayDate, messages: [] };
      }
      
      groups[key].messages.push(m);
    }
    
    return Object.values(groups);
  });

  return { grouped };
}

/**
 * Оптимизированные вычисления для списка сообщений
 */
export function useMessageListOptimizations(_props: { messages: any[] }) {
  const { parseGiftMessage } = useGiftMessageCache();
  const { isImageAttachment } = useAttachmentCache();

  // Мемоизированная сортировка реакций
  const getTopReactions = (reactions: any[] = []): any[] => {
    if (!reactions || reactions.length === 0) return [];
    if (reactions.length <= 2) return reactions;
    
    return reactions
      .slice()
      .sort((a, b) => (b.count || 1) - (a.count || 1))
      .slice(0, 2);
  };

  // Простые классы без вычислений
  const getTypeClass = (type: string): string => {
    return type === 'received' ? 'inbox' : type;
  };

  const getUploadProgress = (attachment: any, item: any): number => {
    const raw =
      (typeof attachment?._uploadProgress === 'number' ? attachment._uploadProgress : undefined) ??
      (typeof item?._uploadProgress === 'number' ? item._uploadProgress : undefined) ??
      0;

    const normalized = Math.round(Number(raw));
    return Math.max(0, Math.min(100, normalized));
  };

  // Утилиты для форматирования
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatTime = (timestamp: number | undefined): string => {
    if (!timestamp) return '';
    
    if (window.TimezoneHelper?.formatTime) {
      const locale = window.TimezoneHelper.getCurrentLocale?.() || 'ru';
      return window.TimezoneHelper.formatTime(timestamp, locale);
    }
    
    const date = new Date(timestamp * 1000);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatDateTime = (timestamp: number | undefined): string => {
    if (!timestamp) return '';
    
    if (window.TimezoneHelper?.formatRelativeTime) {
      return window.TimezoneHelper.formatRelativeTime(timestamp);
    }
    
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    const { t } = useI18n();
    
    if (messageDate.getTime() === today.getTime()) {
      return t('messages.datetime_today', { time: `${hours}:${minutes}` });
    }
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (messageDate.getTime() === yesterday.getTime()) {
      return t('messages.datetime_yesterday', { time: `${hours}:${minutes}` });
    }
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return t('messages.datetime_full', { date: `${day}.${month}.${year}`, time: `${hours}:${minutes}` });
  };

  return {
    parseGiftMessage,
    isImageAttachment,
    getTopReactions,
    getTypeClass,
    getUploadProgress,
    formatFileSize,
    formatTime,
    formatDateTime
  };
}
